import {convertToURLDatetime, parseStartEndDatetimeFromURLParams} from '@domain/shared'
import {
  LayoutOptimizerSummary,
  PageOptimizerActions,
  SelectedOptimizerPlanProvider,
  useOptimizerRange
} from '@features/optimizer'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {PlanningChartStartEndProvider} from '@widgets/planning-schedule-chart'
import React, {useCallback, useLayoutEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {RouteName} from '../../routing'
import {PlannerPageLayout} from '../../shared/components/PlannerPageLayout'
import {PlannerLSSettingsKeys} from '../../shared/enums'
import {pageDataTestId} from '../../shared/formatutils'
import {calculateDatetimeRangeWithinBoundary} from '../../shared/helpers/utils'
import {usePlannerUISettings} from '../../shared/hooks/usePlannerUISettings'
import {useSearchParams} from '../../shared/hooks/useSearchParams'
import {NumberRange} from '../../shared/selectors/time'

import {OptimizerSummaryPageSkeleton} from './OptimizerSummaryPageSkeleton'

export const MIN_RANGE_HOURS = 12
export const MAX_RANGE_HOURS = 24 * 7

export const PageOptimizerSummary: React.FC = () => {
  const {t} = useTranslation()
  const timezoneId = useUpmPlantTimezone()
  const {plantId: plantCode} = useUpmPlantContext()
  const [startOfPlan, endOfPlan] = useOptimizerRange()

  // slider default range
  const {settings: sliderSettings, updateSettings: updateSliderSetting} = usePlannerUISettings(
    PlannerLSSettingsKeys.OptimizerSliderSettings,
    {defaultRange: 24}
  )
  const updateSliderDefaultRange = useCallback(
    (defaultRange: number) => {
      updateSliderSetting({defaultRange})
    },
    [updateSliderSetting]
  )

  const [obtainedParams, setSearchParams] = useSearchParams('start', 'end')

  const parsedSelectedRange = useMemo(() => {
    const parsedRange = parseStartEndDatetimeFromURLParams({...obtainedParams, timezoneId})
    // ensure that url params are within the operator view boundary
    return parsedRange
      ? calculateDatetimeRangeWithinBoundary(parsedRange, [startOfPlan, endOfPlan])
      : undefined
  }, [obtainedParams, startOfPlan, endOfPlan, timezoneId])

  const sliderDefaultRange = sliderSettings.defaultRange

  // if selectedRange is not set, set it to default selected range
  useLayoutEffect(() => {
    if (!parsedSelectedRange) {
      setSearchParams({
        start: convertToURLDatetime(startOfPlan, timezoneId),
        end: convertToURLDatetime(startOfPlan.clone().add(sliderDefaultRange, 'hours'), timezoneId)
      })
    }
  }, [parsedSelectedRange, setSearchParams, startOfPlan, sliderDefaultRange, timezoneId])

  const sliderMinMaxRange = useMemo(() => [MIN_RANGE_HOURS, MAX_RANGE_HOURS] as NumberRange, [])

  if (!parsedSelectedRange) {
    return <OptimizerSummaryPageSkeleton />
  }

  return (
    <PlanningChartStartEndProvider
      sliderDefaultRange={sliderDefaultRange}
      sliderMinMaxRange={sliderMinMaxRange}
      updateSliderDefaultRange={updateSliderDefaultRange}
      startOfPlan={startOfPlan}
      endOfPlan={endOfPlan}
      boundary={[startOfPlan, endOfPlan]}
    >
      <SelectedOptimizerPlanProvider>
        <PlannerPageLayout
          isTourBannerEnabled={true}
          title={t('navItems.optimizer')}
          pageTitle={t('navItems.optimizer')}
          headerActionContent={<PageOptimizerActions />}
          {...pageDataTestId(RouteName.OPTIMIZER)}
        >
          <LayoutOptimizerSummary
            plantCode={plantCode}
            startOfPlan={startOfPlan}
            endOfPlan={endOfPlan}
          />
        </PlannerPageLayout>
      </SelectedOptimizerPlanProvider>
    </PlanningChartStartEndProvider>
  )
}
